import { useEffect, useRef, useState } from 'react'
import { AudioLines, MonitorOff, MonitorUp, Video, VideoOff, X } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { useVoiceStore } from '@/store/voice'
import { publicUser } from '@/lib/auth'
import { cx } from '@/lib/cx'
import { useGuildLimits } from '@/lib/guildLimits'
import { P, basePermissions, channelPermissions, has } from '@/lib/permissions'
import { canShareScreen } from '@/lib/screenShare'
import type { Member } from '@/types/api'
import { ScreenShareModal } from './ScreenShareModal'
import { Soundboard } from './Soundboard'
import { SoundForm } from './SoundForm'
import styles from './VoiceActions.module.css'

/**
 * Ряд кнопок под полосой голоса, как у Discord: камера, демонстрация экрана и звуковая панель —
 * не надо открывать канал, чтобы включить камеру или сыграть звук.
 */
export function VoiceActions() {
  const connection = useVoiceStore((s) => s.connection)
  const camera = useVoiceStore((s) => s.camera)
  const sharing = useVoiceStore((s) => s.sharing)
  const afk = useVoiceStore(s => s.afk)
  const selfDeaf = useVoiceStore((s) => s.selfDeaf)
  const selfMute = useVoiceStore((s) => s.selfMute)
  const sharePrefs = useVoiceStore((s) => s.prefs.screenShare)
  const error = useVoiceStore((s) => s.error)
  const toggleCamera = useVoiceStore((s) => s.toggleCamera)
  const startScreen = useVoiceStore((s) => s.startScreen)
  const stopScreen = useVoiceStore((s) => s.stopScreen)
  const view = useAppStore((s) => s.view)
  const me = useAuthStore((s) => s.user)
  const guildId = connection?.guildId ?? null
  const guild = useGuildStore((s) => (guildId ? s.guilds[guildId] : undefined))
  const channel = useGuildStore((s) => (guildId && connection ? s.channels[guildId]?.[connection.channelId] : undefined))
  const member = useGuildStore((s) => (guildId && me ? s.members[guildId]?.[me.id] : undefined))
  const state = useGuildStore((s) => (guildId && me ? s.voiceStates[guildId]?.[me.id] : undefined))
  const limits = useGuildLimits(guild?.premium_tier)
  const [board, setBoard] = useState(false)
  const [shareModal, setShareModal] = useState(false)
  const [adding, setAdding] = useState(false)
  const boardRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)

  // панель закрывается нажатием мимо неё и по Esc; нажатие на свою кнопку её и так переключает
  useEffect(() => {
    if (!board) return
    const onDown = (e: MouseEvent) => {
      const at = e.target as Node
      if (boardRef.current?.contains(at) || toggleRef.current?.contains(at)) return
      setBoard(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBoard(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [board])

  if (!connection) return null
  const inGuild = Boolean(guildId)
  const self: Member | undefined = me && inGuild ? (member ?? { user: publicUser(me), nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null }) : undefined
  const perms = guild && self ? channelPermissions(guild, self, channel) : 0n
  const guildPerms = guild && self ? basePermissions(guild, self) : 0n
  // в звонке ЛС прав сервера нет: камера и экран доступны всем участникам звонка
  const canCamera = !afk && (!inGuild || has(perms, P.SPEAK))
  const canStream = !afk && canShareScreen && (!inGuild || has(perms, P.STREAM))
  const plus = me?.premium_type === 2
  const blocked = afk ? 'Звуки недоступны в AFK' : selfDeaf || state?.deaf
    ? 'Звук выключен. Включите его кнопкой с наушниками'
    : state?.mute || state?.suppress
      ? 'Микрофон выключен сервером, звуки недоступны'
      : selfMute
        ? 'Микрофон выключен. Включите его, чтобы играть звуки'
        : inGuild && !(has(perms, P.SPEAK) && has(perms, P.USE_SOUNDBOARD))
          ? 'В этом канале нельзя играть звуки'
          : null
  const external = plus && (!inGuild || has(perms, P.USE_EXTERNAL_SOUNDS))
  const canAdd = Boolean(guild) && (has(guildPerms, P.CREATE_EXPRESSIONS) || has(guildPerms, P.MANAGE_GUILD_EXPRESSIONS)) && (guild?.soundboard_sounds?.length ?? 0) < limits.sounds
  // ошибку камеры или экрана комната покажет сама; из дока — здесь, иначе её не видно
  const inRoom = view.kind === 'guild' ? view.channelId === connection.channelId : view.kind === 'home' && 'channelId' in view && view.channelId === connection.channelId

  return (
    <>
      <div className={styles.row}>
        <button
          type="button"
          className={cx(styles.button, camera && styles.on)}
          disabled={!canCamera}
          data-tooltip={afk ? 'Камера недоступна в AFK' : !canCamera ? 'Нет права включать камеру в этом канале' : camera ? 'Выключить камеру' : 'Включить камеру'}
          aria-label={camera ? 'Выключить камеру' : 'Включить камеру'}
          aria-pressed={camera}
          onClick={() => void toggleCamera()}
        >
          {camera ? <VideoOff size={18} /> : <Video size={18} />}
        </button>
        <button
          type="button"
          className={cx(styles.button, sharing && styles.on)}
          disabled={!canStream}
          data-tooltip={afk ? 'Демонстрация недоступна в AFK' : !canShareScreen ? 'Этот браузер не умеет показывать экран' : !canStream ? 'Нет права показывать экран в этом канале' : sharing ? 'Остановить демонстрацию' : 'Демонстрация экрана'}
          aria-label={sharing ? 'Остановить демонстрацию' : 'Демонстрация экрана'}
          aria-pressed={sharing}
          onClick={() => (sharing ? void stopScreen() : setShareModal(true))}
        >
          {sharing ? <MonitorOff size={18} /> : <MonitorUp size={18} />}
        </button>
        <button
          ref={toggleRef}
          type="button"
          className={cx(styles.button, board && styles.open)}
          data-tooltip="Звуковая панель"
          aria-label="Звуковая панель"
          aria-haspopup="dialog"
          aria-expanded={board}
          onClick={() => setBoard((v) => !v)}
        >
          <AudioLines size={18} />
        </button>
      </div>
      {error && !inRoom ? (
        <p className={styles.error} role="alert">
          <span>{error}</span>
          <button type="button" className={styles.dismiss} aria-label="Скрыть ошибку" onClick={() => useVoiceStore.setState({ error: null })}><X size={14} /></button>
        </p>
      ) : null}
      {board ? (
        <Soundboard
          ref={boardRef}
          blocked={blocked}
          external={external}
          onAdd={canAdd ? () => { setBoard(false); setAdding(true) } : null}
        />
      ) : null}
      {shareModal ? <ScreenShareModal initial={sharePrefs} {...(guildId ? { guildId } : {})} onStart={startScreen} onClose={() => setShareModal(false)} /> : null}
      {adding && guild ? <SoundForm guild={guild} onClose={() => setAdding(false)} /> : null}
    </>
  )
}
